import { useFeedbacks, useSkipFeedbackMutation } from "query/task.query";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FeedbackFilterBar } from "./FeedbackFilterBar";
import { FeedbackListItem } from "./FeedbackListItem";
import EmptyState from "components/EmptyState";
import Loader from "components/Loader";
import FeedbackModal from "../task/components/modals/FeedbackModal";

const FeedbackListPage = () => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const { data: feedbacks = [], isLoading } = useFeedbacks();
  const { mutate: skipFeedback } = useSkipFeedbackMutation();

  // Client-side filtering
  const filteredFeedbacks = useMemo(() => {
    if (!searchValue || searchValue.trim() === "") {
      return feedbacks;
    }
    const searchLower = searchValue.toLowerCase();
    return feedbacks.filter(
      (feedback) =>
        feedback.taskCode.toLowerCase().includes(searchLower) ||
        feedback.customerName.toLowerCase().includes(searchLower) ||
        feedback.projectName.toLowerCase().includes(searchLower)
    );
  }, [feedbacks, searchValue]);

  const handleSkip = (taskId: number) => {
    skipFeedback(taskId);
  };

  const handleFillFeedback = (taskId: number) => {
    // Open the feedback modal directly
    setSelectedTaskId(taskId);
  };

  return (
    <div className="flex flex-col">
      <div className="text-color-primary bg-color-base mb-4 border-b border-slate-200 dark:border-slate-600">
        <div className="px-10 py-6">
          <h1 className="text-3xl font-medium leading-tight">
            <span>{t("feedback.page.title")}</span>
          </h1>
          <p className="text-color-secondary text-sm">{t("feedback.page.subtitle")}</p>
        </div>
      </div>
      <div className="m-4">
        <FeedbackFilterBar searchValue={searchValue} onSearchChange={setSearchValue} />

        <Loader isShown={isLoading} />

        {!isLoading && filteredFeedbacks.length === 0 && (
          <EmptyState
            title={t("feedback.page.empty")}
            subtitle={searchValue ? t("feedback.page.noSearchResults") : undefined}
          />
        )}

        {!isLoading && filteredFeedbacks.length > 0 && (
          <div
            className="text-color-base bg-color-base rounded-md text-base shadow"
            data-test="feedbacks-card-container"
          >
            <ul className="divide-y divide-gray-200 dark:divide-slate-600" data-test="feedbacks-cards">
              {filteredFeedbacks.map((feedback) => (
                <li key={feedback.taskId} data-test={`feedback-card-${feedback.taskId}`}>
                  <FeedbackListItem feedback={feedback} onSkip={handleSkip} onFillFeedback={handleFillFeedback} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {selectedTaskId && (
        <FeedbackModal show={true} taskId={selectedTaskId.toString()} onClose={() => setSelectedTaskId(null)} />
      )}
    </div>
  );
};

export default FeedbackListPage;
